/**
 * V86 Carril L — "Arma tu platillo": admin de la BIBLIOTECA de grupos de
 * modificadores (Salsa / Proteína / Extras…) + attach a platillos. TOUCH-FIRST:
 * targets grandes (≥48px), steppers en vez de teclado para min/max, chips para
 * attachar. El costo de las opciones es dato financiero (el API lo strippea sin
 * financials:view). Gateado a módulo gastro + product_catalog:write (la tab
 * solo aparece con write; el API además regresa 403).
 */
import { useEffect, useMemo, useState } from 'react';
import { Check, ChevronDown, ChevronUp, Layers, Pencil, Plus, Trash2, X, Carrot } from 'lucide-react';
import { api } from '../lib/api';

const mxn = (v: any) =>
  `$${Number(v ?? 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const btnPrimary = 'rounded-xl bg-[var(--brand-primary)] px-4 py-2.5 text-sm font-medium text-[var(--brand-primary-fg)] hover:bg-[var(--brand-primary-hover)] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-[var(--brand-primary)]';
const btnGhost = 'rounded-xl bg-white px-4 py-2.5 text-sm font-medium text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white';

export interface ModOption {
  id: string; name: string; priceDelta: number; cost: number | null;
  sortOrder: number; isActive: boolean;
}
export interface ModGroup {
  id: string; name: string; minSelect: number; maxSelect: number | null;
  required: boolean; isActive: boolean; usedByProducts?: number;
  options: ModOption[];
  /** V115 — solo en el GET por platillo: override crudo del attach (null=hereda). */
  minSelectOverride?: number | null;
}
interface ProductLite { id: string; name: string; category: string; isPrixFixe?: boolean; modifierGroups?: { id: string }[]; }

/** Stepper touch (sin teclado): − valor +. Exportado: lo reusa el POS (comensales V117). */
export function Stepper({ value, min, onChange, label }: { value: number; min: number; onChange: (v: number) => void; label: string }) {
  return (
    <div className="flex items-center gap-2">
      <span className="w-24 text-xs text-slate-500">{label}</span>
      <button type="button" onClick={() => onChange(Math.max(min, value - 1))}
        className="grid h-12 w-12 place-items-center rounded-xl bg-slate-100 text-lg font-semibold text-slate-700 active:bg-slate-200">−</button>
      <span className="w-10 text-center text-base font-semibold tabular-nums text-slate-900">{value}</span>
      <button type="button" onClick={() => onChange(value + 1)}
        className="grid h-12 w-12 place-items-center rounded-xl bg-slate-100 text-lg font-semibold text-slate-700 active:bg-slate-200">+</button>
    </div>
  );
}

export function ModifiersView() {
  const [tab, setTab] = useState<'grupos' | 'platillos'>('grupos');
  const [groups, setGroups] = useState<ModGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [editGroup, setEditGroup] = useState<ModGroup | 'new' | null>(null);

  const load = async () => {
    setLoading(true);
    try { setGroups((await api('/gastro/modifier-groups')) as ModGroup[]); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div>
      <div className="mb-5 flex items-end justify-between">
        <div>
          <h2 className="font-serif text-2xl tracking-tight text-slate-900">Modificadores</h2>
          <p className="text-sm text-slate-500">Arma tu platillo: grupos de opciones (salsa, proteína, extras) con precio y costo.</p>
        </div>
        {tab === 'grupos' && (
          <button className={btnPrimary} onClick={() => setEditGroup('new')}>+ Grupo</button>
        )}
      </div>

      <div className="mb-4 flex gap-2">
        {(['grupos', 'platillos'] as const).map((t) => (
          <button key={t} onClick={() => setTab(t)}
            className={`min-h-12 rounded-xl px-5 text-sm font-medium capitalize transition ${tab === t ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
            {t === 'grupos' ? 'Biblioteca de grupos' : 'Platillos'}
          </button>
        ))}
      </div>

      {tab === 'grupos' && (
        loading ? <p className="text-sm text-slate-500">Cargando…</p> : groups.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-200 p-10 text-center text-sm text-slate-400">
            Crea el primer grupo (ej. “Salsa”, elige 1: Verde / Roja) con “+ Grupo”.
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {groups.map((g) => (
              <button key={g.id} onClick={() => setEditGroup(g)}
                className={`flex flex-col items-start gap-2 rounded-2xl border p-4 text-left transition hover:bg-slate-50 ${g.isActive ? 'border-slate-200 bg-white' : 'border-slate-100 bg-slate-50 opacity-60'}`}>
                <div className="flex w-full items-center justify-between">
                  <span className="flex items-center gap-2 font-serif text-lg text-slate-900">
                    <Layers className="h-4 w-4 text-slate-400" /> {g.name}
                    {!g.isActive && <span className="rounded-full bg-slate-200 px-2 py-0.5 text-[10px] font-semibold text-slate-600">Inactivo</span>}
                  </span>
                  <Pencil className="h-4 w-4 text-slate-300" />
                </div>
                <span className="text-[11px] font-medium uppercase tracking-wide text-slate-400">
                  {g.required ? `Obligatorio · elige ${g.minSelect}${g.maxSelect && g.maxSelect !== g.minSelect ? `–${g.maxSelect}` : ''}` : g.maxSelect ? `Opcional · hasta ${g.maxSelect}` : 'Opcional · sin límite'}
                  {` · usado en ${g.usedByProducts ?? 0} platillo${(g.usedByProducts ?? 0) === 1 ? '' : 's'}`}
                </span>
                <div className="flex flex-wrap gap-1.5">
                  {g.options.filter((o) => o.isActive).map((o) => (
                    <span key={o.id} className="rounded-full bg-slate-100 px-2.5 py-1 text-xs text-slate-600">
                      {o.name}{Number(o.priceDelta) > 0 ? ` +${mxn(o.priceDelta)}` : ''}
                    </span>
                  ))}
                  {g.options.filter((o) => o.isActive).length === 0 && (
                    <span className="text-xs text-slate-400">Sin opciones aún</span>
                  )}
                </div>
              </button>
            ))}
          </div>
        )
      )}

      {tab === 'platillos' && <AttachPanel groups={groups.filter((g) => g.isActive)} />}

      {editGroup && (
        <GroupEditorModal
          group={editGroup === 'new' ? null : editGroup}
          onClose={() => setEditGroup(null)}
          onChanged={() => { setEditGroup(null); load(); }}
        />
      )}
    </div>
  );
}

// ===========================================================================
// Editor de grupo + sus opciones (modal touch)
// ===========================================================================
function GroupEditorModal({ group, onClose, onChanged }: {
  group: ModGroup | null; onClose: () => void; onChanged: () => void;
}) {
  const isNew = !group;
  const [name, setName] = useState(group?.name ?? '');
  const [required, setRequired] = useState(group?.required ?? false);
  // min visible solo si required (required == minSelect >= 1; el toggle escribe 0<->1).
  const [minSelect, setMinSelect] = useState(group?.minSelect ?? 0);
  const [noMax, setNoMax] = useState(group ? group.maxSelect == null : true);
  const [maxSelect, setMaxSelect] = useState(group?.maxSelect ?? 1);
  const [isActive, setIsActive] = useState(group?.isActive ?? true);
  const [options, setOptions] = useState<ModOption[]>(group?.options ?? []);
  const [newOpt, setNewOpt] = useState({ name: '', priceDelta: '', cost: '' });
  // Edición inline de una opción existente (name/price/cost).
  const [editOpt, setEditOpt] = useState<{ id: string; name: string; priceDelta: string; cost: string } | null>(null);
  // V127 — editor de receta (insumos) de una opción.
  const [recipeOpt, setRecipeOpt] = useState<ModOption | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const toggleRequired = (r: boolean) => {
    setRequired(r);
    setMinSelect((m) => (r ? Math.max(1, m) : 0));
  };

  const saveGroup = async () => {
    setBusy(true); setErr(null);
    try {
      const body = {
        name: name.trim(),
        minSelect: required ? Math.max(1, minSelect) : 0,
        maxSelect: noMax ? null : Math.max(maxSelect, Math.max(required ? minSelect : 0, 1)),
        ...(group ? { isActive } : {}),
      };
      if (isNew) await api('/gastro/modifier-groups', { method: 'POST', body });
      else await api(`/gastro/modifier-groups/${group!.id}`, { method: 'PATCH', body });
      onChanged();
    } catch (e: any) {
      setErr(e?.body?.message || e?.message || 'No se pudo guardar');
      setBusy(false);
    }
  };

  const addOption = async () => {
    if (!group || !newOpt.name.trim()) return;
    setBusy(true); setErr(null);
    try {
      const created = (await api(`/gastro/modifier-groups/${group.id}/options`, {
        method: 'POST',
        body: {
          name: newOpt.name.trim(),
          priceDelta: Number(newOpt.priceDelta) || 0,
          ...(newOpt.cost !== '' ? { cost: Number(newOpt.cost) || 0 } : {}),
        },
      })) as ModOption;
      setOptions((xs) => [...xs, created]);
      setNewOpt({ name: '', priceDelta: '', cost: '' });
    } catch (e: any) {
      setErr(e?.body?.message || e?.message || 'No se pudo agregar la opción');
    } finally { setBusy(false); }
  };

  const patchOption = async (id: string, patch: Record<string, unknown>): Promise<boolean> => {
    setBusy(true); setErr(null);
    try {
      const updated = (await api(`/gastro/modifier-groups/options/${id}`, { method: 'PATCH', body: patch })) as ModOption;
      setOptions((xs) => xs.map((o) => (o.id === id ? { ...o, ...updated } : o)));
      return true;
    } catch (e: any) {
      setErr(e?.body?.message || e?.message || 'No se pudo actualizar');
      return false;
    } finally { setBusy(false); }
  };

  const saveEdit = async () => {
    if (!editOpt || !editOpt.name.trim()) return;
    await patchOption(editOpt.id, {
      name: editOpt.name.trim(),
      priceDelta: Number(editOpt.priceDelta) || 0,
      // costo vacío = null (sin costo capturado → costMissing en margen).
      cost: editOpt.cost === '' ? null : Number(editOpt.cost) || 0,
    });
    setEditOpt(null);
  };

  // Borrado FÍSICO (los snapshots de líneas vendidas quedan intactos — FK SET NULL).
  // Para ocultar temporalmente del POS sin perderla está "Desactivar".
  const deleteOption = async (o: ModOption) => {
    if (!window.confirm(`¿Borrar la opción “${o.name}”? Los tickets ya vendidos no cambian.`)) return;
    setBusy(true); setErr(null);
    try {
      await api(`/gastro/modifier-groups/options/${o.id}`, { method: 'DELETE' });
      setOptions((xs) => xs.filter((x) => x.id !== o.id));
    } catch (e: any) {
      setErr(e?.body?.message || e?.message || 'No se pudo borrar');
    } finally { setBusy(false); }
  };

  // Reorden por botones ↑/↓ (drag es frágil en touch): swap de sortOrder en el
  // server y LUEGO el swap visual — si algún PATCH falla, no se miente en la UI.
  const move = async (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= options.length) return;
    const a = options[i]; const b = options[j];
    if (!(await patchOption(a.id, { sortOrder: b.sortOrder }))) return;
    if (!(await patchOption(b.id, { sortOrder: a.sortOrder }))) return;
    setOptions((xs) => {
      const copy = [...xs];
      [copy[i], copy[j]] = [copy[j], copy[i]];
      return copy;
    });
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-slate-900/40 p-4" onClick={onClose}>
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-center justify-between">
          <h3 className="font-serif text-lg text-slate-900">{isNew ? 'Nuevo grupo' : `Editar “${group!.name}”`}</h3>
          <button onClick={onClose} className="grid h-10 w-10 place-items-center text-slate-400 hover:text-slate-700"><X className="h-5 w-5" /></button>
        </div>

        <input className="input mb-4 min-h-12 text-base" placeholder="Nombre (ej. Salsa, Proteína, Extras)"
          value={name} onChange={(e) => setName(e.target.value)} />

        <div className="mb-3 flex min-h-12 items-center justify-between rounded-xl bg-slate-50 px-4">
          <span className="text-sm font-medium text-slate-700">Obligatorio (el cajero DEBE elegir)</span>
          <button type="button" onClick={() => toggleRequired(!required)}
            className={`h-8 w-14 rounded-full p-1 transition ${required ? 'bg-slate-900' : 'bg-slate-300'}`}>
            <span className={`block h-6 w-6 rounded-full bg-white transition ${required ? 'translate-x-6' : ''}`} />
          </button>
        </div>
        {required && (
          <div className="mb-3"><Stepper label="Mínimo a elegir" value={minSelect} min={1} onChange={setMinSelect} /></div>
        )}
        <div className="mb-3 flex min-h-12 items-center justify-between rounded-xl bg-slate-50 px-4">
          <span className="text-sm font-medium text-slate-700">Sin límite de selección</span>
          <button type="button" onClick={() => setNoMax(!noMax)}
            className={`h-8 w-14 rounded-full p-1 transition ${noMax ? 'bg-slate-900' : 'bg-slate-300'}`}>
            <span className={`block h-6 w-6 rounded-full bg-white transition ${noMax ? 'translate-x-6' : ''}`} />
          </button>
        </div>
        {!noMax && (
          <div className="mb-3"><Stepper label="Máximo a elegir" value={maxSelect} min={Math.max(required ? minSelect : 0, 1)} onChange={setMaxSelect} /></div>
        )}
        {!isNew && (
          <div className="mb-4 flex min-h-12 items-center justify-between rounded-xl bg-slate-50 px-4">
            <span className="text-sm font-medium text-slate-700">Activo</span>
            <button type="button" onClick={() => setIsActive(!isActive)}
              className={`h-8 w-14 rounded-full p-1 transition ${isActive ? 'bg-slate-900' : 'bg-slate-300'}`}>
              <span className={`block h-6 w-6 rounded-full bg-white transition ${isActive ? 'translate-x-6' : ''}`} />
            </button>
          </div>
        )}

        {/* Opciones (solo en grupos ya creados: necesitan el id).
            Layout en GRID con tracks fijos: la clase .input trae width:100% y en un
            flex row hacía colapsar el input del NOMBRE (bug del smoke 2026-07-13) —
            con grid cada input llena SU columna y no pelea con las demás. */}
        {!isNew && (
          <>
            <p className="mb-2 text-xs uppercase tracking-wide text-slate-400">Opciones</p>
            <div className="mb-3 space-y-2">
              {options.map((o, i) => (
                editOpt?.id === o.id ? (
                  /* Edición inline: nombre / +$ / costo / guardar / cancelar */
                  <div key={o.id} className="grid grid-cols-[minmax(0,1fr)_5.5rem_5.5rem_3rem_3rem] items-center gap-2 rounded-xl border border-slate-900 px-2 py-2">
                    <input className="input min-h-12" placeholder="Nombre de la opción" autoFocus
                      value={editOpt.name} onChange={(e) => setEditOpt({ ...editOpt, name: e.target.value })} />
                    <input className="input min-h-12" type="number" min="0" step="0.01" placeholder="+$"
                      value={editOpt.priceDelta} onChange={(e) => setEditOpt({ ...editOpt, priceDelta: e.target.value })} />
                    <input className="input min-h-12" type="number" min="0" step="0.01" placeholder="costo"
                      value={editOpt.cost} onChange={(e) => setEditOpt({ ...editOpt, cost: e.target.value })} />
                    <button onClick={saveEdit} disabled={busy || !editOpt.name.trim()} title="Guardar"
                      className="grid h-12 w-12 place-items-center rounded-xl bg-[var(--brand-primary)] text-[var(--brand-primary-fg)] disabled:opacity-40">
                      <Check className="h-5 w-5" />
                    </button>
                    <button onClick={() => setEditOpt(null)} disabled={busy} title="Cancelar"
                      className="grid h-12 w-12 place-items-center rounded-xl bg-slate-100 text-slate-600">
                      <X className="h-5 w-5" />
                    </button>
                  </div>
                ) : (
                  <div key={o.id} className={`flex items-center gap-2 rounded-xl border px-3 py-2 ${o.isActive ? 'border-slate-200' : 'border-slate-100 opacity-50'}`}>
                    <div className="flex flex-col">
                      <button onClick={() => move(i, -1)} disabled={busy || i === 0} title="Subir"
                        className="grid h-6 w-8 place-items-center text-slate-300 hover:text-slate-600 disabled:opacity-30"><ChevronUp className="h-4 w-4" /></button>
                      <button onClick={() => move(i, 1)} disabled={busy || i === options.length - 1} title="Bajar"
                        className="grid h-6 w-8 place-items-center text-slate-300 hover:text-slate-600 disabled:opacity-30"><ChevronDown className="h-4 w-4" /></button>
                    </div>
                    <span className="min-w-0 flex-1 truncate text-sm font-medium text-slate-800">{o.name}</span>
                    <span className="text-xs tabular-nums text-slate-500">{Number(o.priceDelta) > 0 ? `+${mxn(o.priceDelta)}` : '—'}</span>
                    <span className="text-[11px] tabular-nums text-slate-400" title="Costo (margen)">
                      {o.cost != null ? `costo ${mxn(o.cost)}` : 'sin costo'}
                    </span>
                    <button onClick={() => setEditOpt({
                      id: o.id, name: o.name,
                      priceDelta: String(Number(o.priceDelta) || 0),
                      cost: o.cost != null ? String(Number(o.cost)) : '',
                    })} disabled={busy} title="Editar nombre / precio / costo"
                      className="grid h-10 w-10 place-items-center rounded-lg text-slate-400 ring-1 ring-slate-200 hover:bg-slate-50">
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button onClick={() => setRecipeOpt(o)} disabled={busy} title="Receta de insumos (food-cost + descuento de stock al vender)"
                      className="grid h-10 w-10 place-items-center rounded-lg text-slate-400 ring-1 ring-slate-200 hover:bg-slate-50">
                      <Carrot className="h-4 w-4" />
                    </button>
                    <button onClick={() => patchOption(o.id, { isActive: !o.isActive })} disabled={busy}
                      title={o.isActive ? 'Ocultar del POS sin borrarla' : 'Volver a mostrar en el POS'}
                      className="rounded-lg px-2 py-2.5 text-[11px] font-medium text-slate-500 ring-1 ring-slate-200 hover:bg-slate-50">
                      {o.isActive ? 'Desactivar' : 'Activar'}
                    </button>
                    <button onClick={() => deleteOption(o)} disabled={busy} title="Borrar (los tickets vendidos no cambian)"
                      className="grid h-10 w-10 place-items-center rounded-lg text-slate-400 ring-1 ring-slate-200 hover:bg-rose-50 hover:text-rose-600">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                )
              ))}
              {options.length === 0 && (
                <p className="text-xs text-slate-400">Sin opciones aún — captura la primera abajo.</p>
              )}
            </div>

            {/* Alta de opción: nombre / +$ / costo / + — se pueden capturar varias seguidas. */}
            <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Nueva opción</p>
            <div className="grid grid-cols-[minmax(0,1fr)_5.5rem_5.5rem_3rem] items-center gap-2">
              <input className="input min-h-12" placeholder="Nombre de la opción (ej. Pollo)"
                value={newOpt.name} onChange={(e) => setNewOpt({ ...newOpt, name: e.target.value })}
                onKeyDown={(e) => { if (e.key === 'Enter' && newOpt.name.trim()) addOption(); }} />
              <input className="input min-h-12" type="number" min="0" step="0.01" placeholder="+$"
                value={newOpt.priceDelta} onChange={(e) => setNewOpt({ ...newOpt, priceDelta: e.target.value })} />
              <input className="input min-h-12" type="number" min="0" step="0.01" placeholder="costo"
                value={newOpt.cost} onChange={(e) => setNewOpt({ ...newOpt, cost: e.target.value })} />
              <button onClick={addOption} disabled={busy || !newOpt.name.trim()} title="Agregar opción"
                className="grid h-12 w-12 place-items-center rounded-xl bg-[var(--brand-primary)] text-[var(--brand-primary-fg)] disabled:opacity-40">
                <Plus className="h-5 w-5" />
              </button>
            </div>
            <p className="mb-4 mt-1 text-[10px] text-slate-400">
              Nombre · precio extra (+$) · costo (para margen, opcional). El “+” agrega y limpia la fila para capturar la siguiente.
            </p>
          </>
        )}
        {isNew && <p className="mb-4 text-xs text-slate-400">Guarda el grupo y vuelve a abrirlo para capturar sus opciones.</p>}

        {err && <p className="alert-over mb-3 rounded-xl px-3 py-2 text-xs">{err}</p>}
        <div className="flex justify-end gap-2">
          <button className={btnGhost} onClick={onClose} disabled={busy}>Cerrar</button>
          <button className={btnPrimary} onClick={saveGroup} disabled={busy || !name.trim()}>
            {busy ? 'Guardando…' : 'Guardar grupo'}
          </button>
        </div>
        {recipeOpt && <OptionRecipeModal option={recipeOpt} onClose={() => setRecipeOpt(null)} />}
      </div>
    </div>
  );
}

// ===========================================================================
// V127 — Receta de insumos de UNA opción (food-cost calculado + descuento de
// stock al vender). Espejo del RecipeModal de producto, contra /insumos/recipe-option.
// ===========================================================================
export function OptionRecipeModal({ option, onClose }: { option: { id: string; name: string }; onClose: () => void }) {
  const [insumos, setInsumos] = useState<any[]>([]);
  const [lines, setLines] = useState<Array<{ insumoId: string; qty: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    Promise.all([api('/insumos'), api(`/insumos/recipe-option/${option.id}`)])
      .then(([ins, rec]: any) => {
        if (cancelled) return;
        setInsumos(ins as any[]);
        setLines((rec as any[]).map((r) => ({ insumoId: r.insumoId, qty: String(r.qty) })));
      })
      .catch(() => { if (!cancelled) setErr('No se pudo cargar la receta'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [option.id]);

  const addLine = () => setLines([...lines, { insumoId: insumos[0]?.id ?? '', qty: '1' }]);
  const setLine = (idx: number, patch: Partial<{ insumoId: string; qty: string }>) =>
    setLines(lines.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  const removeLine = (idx: number) => setLines(lines.filter((_, i) => i !== idx));
  const unitOf = (id: string) => insumos.find((i) => i.id === id)?.unit ?? '';

  const save = async () => {
    setErr(null); setBusy(true);
    const items = lines.filter((l) => l.insumoId && Number(l.qty) > 0).map((l) => ({ insumoId: l.insumoId, qty: Number(l.qty) }));
    try { await api(`/insumos/recipe-option/${option.id}`, { method: 'PUT', body: { items } }); onClose(); }
    catch (e: any) { setErr(e?.body?.message || e?.message || 'No se pudo guardar la receta'); setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] grid place-items-center bg-slate-900/40 p-4" onClick={onClose}>
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="mb-3 flex items-center justify-between">
          <h3 className="font-serif text-lg text-slate-900">Receta — {option.name}</h3>
          <button onClick={onClose} className="grid h-10 w-10 place-items-center text-slate-400 hover:text-slate-700"><X className="h-5 w-5" /></button>
        </div>
        <p className="mb-3 text-xs text-slate-500">Define qué insumos consume <strong>una unidad</strong> de esta opción. Al venderla se descuentan del inventario (sumando a la receta del platillo) y su <strong>food-cost se calcula</strong> de los insumos.</p>
        {loading ? <p className="text-sm text-slate-400">Cargando…</p> : insumos.length === 0 ? (
          <p className="text-sm text-slate-500">Primero crea insumos en la pestaña <strong>Insumos</strong>.</p>
        ) : (
          <div className="space-y-2">
            {lines.length === 0 && <p className="text-sm text-slate-400">Sin insumos en la receta. Sin receta, se usa el costo plano de la opción.</p>}
            {lines.map((l, idx) => {
              const known = insumos.some((i) => i.id === l.insumoId);
              return (
                <div key={idx} className="flex items-center gap-2">
                  <select className="input min-w-[10rem] flex-1" value={l.insumoId} onChange={(e) => setLine(idx, { insumoId: e.target.value })}>
                    {!known && <option value={l.insumoId}>{l.insumoId ? '(insumo no disponible — elige otro)' : '— Elige un insumo —'}</option>}
                    {insumos.map((i) => <option key={i.id} value={i.id}>{i.name} ({i.unit})</option>)}
                  </select>
                  <input className="input w-20 shrink-0" type="number" step="any" min="0" value={l.qty} onChange={(e) => setLine(idx, { qty: e.target.value })} />
                  <span className="w-8 shrink-0 text-xs text-slate-400">{unitOf(l.insumoId)}</span>
                  <button type="button" className={`${btnGhost} shrink-0`} onClick={() => removeLine(idx)}>Quitar</button>
                </div>
              );
            })}
            <button type="button" className={btnGhost} onClick={addLine}>+ Agregar insumo</button>
          </div>
        )}
        {err && <p className="alert-over mt-3 rounded-xl px-3 py-2 text-xs">{err}</p>}
        <div className="mt-4 flex justify-end gap-2">
          <button type="button" className={btnGhost} onClick={onClose}>Cancelar</button>
          <button type="button" className={btnPrimary} disabled={busy || loading || insumos.length === 0} onClick={save}>{busy ? 'Guardando…' : 'Guardar receta'}</button>
        </div>
      </div>
    </div>
  );
}

// ===========================================================================
// Attach: qué grupos usa cada platillo (chips touch, orden = orden de selección)
// ===========================================================================
/** V115 — attach con el override obligatorio/opcional POR PLATILLO. */
interface AttachedRow { id: string; override: number | null } // null=hereda, 0=opcional, 1=obligatorio

function AttachPanel({ groups }: { groups: ModGroup[] }) {
  const [products, setProducts] = useState<ProductLite[]>([]);
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState<ProductLite | null>(null);
  const [attached, setAttached] = useState<AttachedRow[]>([]); // en orden
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        // V126 (#310) — los PAQUETES prix-fixe se administran en su propia pantalla
        // (Paquetes), no como "arma tu platillo": fuera de este selector.
        const all = (await api('/boutique/products')) as ProductLite[];
        setProducts(all.filter((p) => !p.isPrixFixe));
      } finally { setLoading(false); }
    })();
  }, []);

  const open = async (p: ProductLite) => {
    setSel(p); setMsg(null);
    const gs = (await api(`/gastro/products/${p.id}/modifier-groups`)) as ModGroup[];
    setAttached(gs.map((g) => ({ id: g.id, override: g.minSelectOverride ?? null })));
  };

  const toggle = (gid: string) =>
    setAttached((xs) => (xs.some((x) => x.id === gid)
      ? xs.filter((x) => x.id !== gid)
      : [...xs, { id: gid, override: null }]));

  const setOverride = (gid: string, override: number | null) =>
    setAttached((xs) => xs.map((x) => (x.id === gid ? { ...x, override } : x)));

  const move = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= attached.length) return;
    setAttached((xs) => {
      const copy = [...xs];
      [copy[i], copy[j]] = [copy[j], copy[i]];
      return copy;
    });
  };

  const save = async () => {
    if (!sel) return;
    setBusy(true); setMsg(null);
    try {
      await api(`/gastro/products/${sel.id}/modifier-groups`, {
        method: 'PUT',
        body: { groups: attached.map((a) => ({ groupId: a.id, minSelectOverride: a.override })) },
      });
      setMsg('Guardado. El POS ya ofrece el armado de este platillo.');
    } catch (e: any) {
      setMsg(e?.body?.message || e?.message || 'No se pudo guardar');
    } finally { setBusy(false); }
  };

  const nameOf = useMemo(() => new Map(groups.map((g) => [g.id, g.name])), [groups]);

  if (loading) return <p className="text-sm text-slate-500">Cargando…</p>;

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
      <div className="space-y-2">
        {products.map((p) => (
          <button key={p.id} onClick={() => open(p)}
            className={`flex min-h-12 w-full items-center justify-between rounded-xl border px-4 text-left transition ${sel?.id === p.id ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-200 bg-white text-slate-800 hover:bg-slate-50'}`}>
            <span className="text-sm font-medium">{p.name}</span>
            <span className={`text-[11px] ${sel?.id === p.id ? 'text-slate-300' : 'text-slate-400'}`}>{p.category}</span>
          </button>
        ))}
        {products.length === 0 && <p className="text-sm text-slate-400">Sin productos en el catálogo.</p>}
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        {!sel ? (
          <p className="py-8 text-center text-sm text-slate-400">Elige un platillo para asignarle grupos.</p>
        ) : (
          <>
            <h3 className="mb-1 font-serif text-lg text-slate-900">{sel.name}</h3>
            <p className="mb-3 text-xs text-slate-500">Toca para agregar/quitar grupos; ordena con las flechas. Por platillo puedes marcar cada grupo <strong>obligatorio</strong> u <strong>opcional</strong>.</p>
            {/* Attachados, en orden */}
            <div className="mb-3 space-y-2">
              {attached.map((a, i) => {
                const g = groups.find((x) => x.id === a.id);
                const heredaLabel = g ? (g.minSelect >= 1 ? `obligatorio, elige ${g.minSelect}` : 'opcional') : '—';
                return (
                  <div key={a.id} className="flex min-h-12 items-center gap-2 rounded-xl bg-slate-900 px-3 py-1.5 text-white">
                    <div className="flex flex-col">
                      <button onClick={() => move(i, -1)} disabled={i === 0} className="text-slate-400 hover:text-white disabled:opacity-30"><ChevronUp className="h-4 w-4" /></button>
                      <button onClick={() => move(i, 1)} disabled={i === attached.length - 1} className="text-slate-400 hover:text-white disabled:opacity-30"><ChevronDown className="h-4 w-4" /></button>
                    </div>
                    <span className="flex-1 text-sm font-medium">{i + 1}. {nameOf.get(a.id) ?? '—'}</span>
                    {/* V115 — obligatorio/opcional POR PLATILLO (null = como el grupo) */}
                    <select
                      value={a.override === null ? '' : String(a.override)}
                      onChange={(e) => setOverride(a.id, e.target.value === '' ? null : Number(e.target.value))}
                      className="rounded-lg border-0 bg-white px-2 py-1.5 text-xs font-medium text-slate-900"
                      style={{ colorScheme: 'light' }}>
                      <option value="">Como el grupo ({heredaLabel})</option>
                      <option value="1">Obligatorio (elige ≥1)</option>
                      <option value="0">Opcional</option>
                    </select>
                    <button onClick={() => toggle(a.id)} className="text-slate-400 hover:text-white"><Trash2 className="h-4 w-4" /></button>
                  </div>
                );
              })}
              {attached.length === 0 && <p className="text-xs text-slate-400">Sin grupos: el platillo se agrega directo (un tap), como hoy.</p>}
            </div>
            {/* Biblioteca restante */}
            <div className="mb-4 flex flex-wrap gap-2">
              {groups.filter((g) => !attached.some((a) => a.id === g.id)).map((g) => (
                <button key={g.id} onClick={() => toggle(g.id)}
                  className="min-h-12 rounded-xl bg-slate-100 px-4 text-sm font-medium text-slate-700 hover:bg-slate-200">
                  + {g.name}
                </button>
              ))}
            </div>
            {msg && <p className="mb-3 rounded-xl bg-slate-50 px-3 py-2 text-xs text-slate-600">{msg}</p>}
            <button className={`${btnPrimary} w-full`} onClick={save} disabled={busy}>
              {busy ? 'Guardando…' : 'Guardar asignación'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
